/**
 * AgreementModern - MIGRATED to use CRUDPage template
 * 
 * Before: 528 lines
 * After: ~220 lines (58% reduction!)
 * 
 * Manages service agreements/contracts with vendors.
 */

import React, { useState, useEffect } from 'react';
import { Box, Chip, Link } from '@mui/material';
import { FileText, Calendar, User } from 'lucide-react';

import CRUDPage from '../Components/UI/CRUDPage';
import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';
import axios from 'axios';
import { getAuth } from 'firebase/auth';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Column definitions
const columns = [
  {
    field: 'contract_code',
    headerName: 'Agreement Code',
    sortable: true,
    renderCell: ({ value }) => (
      <Chip
        icon={<FileText size={14} />}
        label={value || 'N/A'}
        size="small"
        variant="outlined"
        color="primary"
        sx={{ fontWeight: 500 }}
      />
    )
  },
  {
    field: 'name',
    headerName: 'Agreement Name',
    sortable: true,
    renderCell: ({ value }) => (
      <Box sx={{ fontWeight: 500, color: 'text.primary' }}>
        {value || 'N/A'}
      </Box>
    )
  },
  {
    field: 'service_provider_name',
    headerName: 'Vendor',
    sortable: true,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <User size={14} color="#666" />
        {value || 'N/A'}
      </Box>
    )
  },
  {
    field: 'contract_type',
    headerName: 'Type',
    sortable: true,
    renderCell: ({ value }) => (
      <Chip
        label={value || 'N/A'}
        size="small"
        color="info"
        variant="filled"
        sx={{ fontWeight: 500 }}
      />
    )
  },
  {
    field: 'valid_from',
    headerName: 'Start Date',
    sortable: true,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
        <Calendar size={14} color="#6BA368" />
        {formatDate(value)}
      </Box>
    )
  },
  {
    field: 'valid_to',
    headerName: 'End Date',
    sortable: true,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
        <Calendar size={14} color="#D2726A" />
        {formatDate(value)}
      </Box>
    )
  },
  {
    field: 'budget_total',
    headerName: 'Value',
    sortable: true,
    align: 'left',
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, color: 'success.main' }}>
        {value ? Number(value).toLocaleString() : 'N/A'}
      </Box>
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    align: 'right'
  }
];

function AgreementModern() {
  const { selectedOrgId, loading: orgLoading } = useOrg();
  const [fieldOptions, setFieldOptions] = useState({
    providers: [],
    members: [],
    contractTypes: [],
    serviceTypes: []
  });

  // Fetch dropdown options.
  // NOTE on the "Agreement Responsible Person" dropdown: this is a dependent
  // field (its options should ideally be scoped to the vendor selected in
  // `service_provider_id`). The shared FormDialog (src/Components/Forms/FormDialog.jsx)
  // owns its own internal react-hook-form instance and does not expose live
  // watched field values (or an onChange callback) back to the page that
  // supplies `fields` — CRUDPage just passes a static `fields` array through.
  // Adding that would mean calling react-hook-form's `useWatch` conditionally
  // inside the per-field render helper, which breaks rules-of-hooks, or
  // reworking the shared FormDialog used by every CRUD page in the app.
  // That's out of scope for a surgical, single-page change, so we use the
  // documented fallback instead: fetch members for every org vendor once and
  // label each option "member_name (vendor_name)" so the vendor is still
  // obvious without a live dependent filter.
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const user = getAuth().currentUser;
        const token = await user.getIdToken();
        const authHeader = { Authorization: `Bearer ${token}` };

        const [providerRes, typeRes, assetTypeRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/providers?organisation_id=${selectedOrgId}`, {
            headers: authHeader
          }),
          axios.get(`${BASE_URL}/api/contract-types-options`, {
            headers: authHeader
          }),
          axios.get(`${BASE_URL}/api/asset-types`, {
            headers: authHeader
          })
        ]);

        const providers = providerRes.data || [];
        const memberResponses = await Promise.all(
          providers.map(p =>
            axios
              .get(`${BASE_URL}/api/service-providers/${p.id}/members`, {
                params: { organisation_id: selectedOrgId },
                headers: authHeader
              })
              .catch(() => ({ data: [] }))
          )
        );
        const members = memberResponses.flatMap((res, idx) => {
          const vendorName = providers[idx].service_provider_name;
          return (res.data || [])
            .filter(m => m.is_active !== false)
            .map(m => ({ value: m.id, label: `${m.member_name} (${vendorName})` }));
        });

        setFieldOptions({
          providers: providers.map(p => ({ value: p.id, label: p.service_provider_name })),
          members,
          contractTypes: typeRes.data.map(t => ({ value: t.id, label: t.name })),
          serviceTypes: assetTypeRes.data.map(s => ({ value: s.id, label: s.name }))
        });
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
      }
    };

    if (!orgLoading && selectedOrgId) {
      fetchOptions();
    }
  }, [selectedOrgId, orgLoading]);

  // Field definitions with dynamic options
  const agreementFields = [
    {
      name: 'contract_code',
      label: 'Agreement Code',
      type: 'text',
      disabled: true,
      helperText: 'Auto-generated'
    },
    {
      name: 'name',
      label: 'Agreement Name',
      type: 'text',
      required: true,
      validation: {
        required: 'Agreement name is required',
        minLength: { value: 3, message: 'Name must be at least 3 characters' }
      }
    },
    {
      name: 'valid_from',
      label: 'Start Date',
      type: 'date',
      required: true,
      validation: {
        required: 'Start date is required'
      }
    },
    {
      name: 'valid_to',
      label: 'End Date',
      type: 'date',
      required: true,
      validation: {
        required: 'End date is required'
      }
    },
    {
      name: 'budget_total',
      label: 'Agreement Value',
      type: 'number',
      required: true,
      validation: {
        required: 'Agreement value is required',
        min: { value: 0, message: 'Value must be positive' }
      }
    },
    {
      name: 'contract_type_id',
      label: 'Agreement Type',
      type: 'select',
      required: true,
      validation: {
        required: 'Agreement type is required'
      },
      options: fieldOptions.contractTypes
    },
    {
      name: 'service_provider_id',
      label: 'Vendor',
      type: 'select',
      required: true,
      validation: {
        required: 'Vendor is required'
      },
      options: fieldOptions.providers
    },
    {
      name: 'service_type_ids',
      label: 'Service Types',
      type: 'multiselect',
      required: true,
      validation: {
        required: 'At least one service type is required'
      },
      options: fieldOptions.serviceTypes
    },
    {
      name: 'extension_date',
      label: 'Agreement Extension Date',
      type: 'date'
    },
    {
      name: 'responsible_member_id',
      label: 'Agreement Responsible Person',
      type: 'select',
      required: true,
      validation: {
        required: 'Responsible person is required'
      },
      helperText: 'Vendor member responsible for this agreement',
      options: fieldOptions.members
    }
  ];

  return (
    <CRUDPage
      title="Agreement Management"
      subtitle="Manage service agreements and contracts with vendors"
      icon={FileText}
      columns={columns}
      fields={agreementFields}
      searchFields={['name', 'contract_code', 'service_provider_name', 'contract_type']}
      permissions={{
        resource: 'Agreement',
        module: 'agreementCreation'
      }}
      crudConfig={{
        endpoint: '/api/contracts',
        entityName: 'Agreement',
        entityNamePlural: 'Agreements',
        useOrgId: true, // ✅ Enable to append organisation_id to GET requests
        transformForEdit: (data) => ({
          ...data,
          service_type_ids: Array.isArray(data.service_type_ids) 
            ? data.service_type_ids 
            : (data.service_type_ids ? [data.service_type_ids] : [])
        }),
        transformCreate: (formData) => ({
          ...formData,
          organisation_id: selectedOrgId
        }),
        transformUpdate: (formData) => ({
          ...formData,
          organisation_id: selectedOrgId
        })
      }}
      emptyMessage="No agreements found. Create your first agreement to track service contracts with vendors."
    />
  );
}

export default AgreementModern;
